/**
 * useBracket hook
 *
 * Evaluates the Commander bracket for the current deck by calling POST /api/deck/bracket.
 * Results are cached in the deck store so they persist across tab switches.
 * Auto-fetches when card names change.
 */

import { useEffect, useMemo, useRef } from 'react';
import type { BracketLabel, ComboResult } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../stores/deck-store.js';
import { api } from '../lib/api-client.js';

export interface BracketState {
  bracket: BracketLabel | null;
  gameChangers: string[];
  combos: ComboResult[];
  isLoading: boolean;
  comboDetectionAvailable: boolean;
  extraTurnCards: string[];
  massLandDenialCards: string[];
  error: string | null;
}

export function useBracket(): BracketState {
  const entries = useDeckStore((s) => s.entries);
  const bracketResult = useDeckStore((s) => s.bracketResult);
  const bracketCardNamesKey = useDeckStore((s) => s.bracketCardNamesKey);
  const isBracketLoading = useDeckStore((s) => s.isBracketLoading);
  const bracketError = useDeckStore((s) => s.bracketError);
  const setBracketResult = useDeckStore((s) => s.setBracketResult);
  const setBracketLoading = useDeckStore((s) => s.setBracketLoading);
  const setBracketError = useDeckStore((s) => s.setBracketError);

  const sortedCardNames = useMemo(
    () =>
      [...entries]
        .map((e) => e.card.name)
        .sort((a, b) => a.localeCompare(b)),
    [entries],
  );

  const cardNamesKey = useMemo(() => JSON.stringify(sortedCardNames), [sortedCardNames]);

  const currentCallRef = useRef<number>(0);

  useEffect(() => {
    if (sortedCardNames.length === 0) return;

    // Skip if we already have results for this exact card set
    if (cardNamesKey === bracketCardNamesKey && bracketResult !== null) return;

    const callId = ++currentCallRef.current;

    setBracketLoading(true);

    api
      .evaluateBracket(sortedCardNames)
      .then((response) => {
        if (callId !== currentCallRef.current) return;
        setBracketResult(response, cardNamesKey);
      })
      .catch(() => {
        if (callId !== currentCallRef.current) return;
        setBracketError('Bracket evaluation failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNamesKey]);

  if (sortedCardNames.length === 0) {
    return {
      bracket: null,
      gameChangers: [],
      combos: [],
      isLoading: false,
      comboDetectionAvailable: false,
      extraTurnCards: [],
      massLandDenialCards: [],
      error: null,
    };
  }

  return {
    bracket: bracketResult?.bracket ?? null,
    gameChangers: bracketResult?.gameChangers ?? [],
    combos: bracketResult?.combos ?? [],
    isLoading: isBracketLoading,
    comboDetectionAvailable: bracketResult?.comboDetectionAvailable ?? false,
    extraTurnCards: bracketResult?.extraTurnCards ?? [],
    massLandDenialCards: bracketResult?.massLandDenialCards ?? [],
    error: bracketError,
  };
}
